/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   FleetHealthTile.tsx
 *     Feature         :   254-pipeline-monitoring-overhaul (US4)
 *     Description     :   Tier-weighted fleet-health score (FR-007a/028) — a SECONDARY read
 *                         below the attention feed. Shows the score against its healthy band
 *                         so a bare number is never presented without context. An explicit
 *                         "nothing monitored" state when the fleet is empty/all-retired
 *                         (FR-032, never a fabricated 100%). Kit + tokens.
 *     Constitution    :   Principle XLV, XXXVII
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-24
 * =========================================================================================
 */
import { Card } from '@/components/ui';
import type { FleetHealth } from '@shared/types/pipelineMonitoring';

export function FleetHealthTile({ health }: { health: FleetHealth | null | undefined }): JSX.Element {
  if (!health) {
    return (
      <Card title="Fleet health">
        <div className="py-4 text-center text-sm text-[var(--color-text-secondary)]">Nothing is being monitored yet.</div>
      </Card>
    );
  }
  const healthy = health.score >= health.healthyBand.min;
  const color = healthy ? 'var(--color-status-success)' : health.score >= 70 ? 'var(--color-status-warning)' : 'var(--color-status-danger)';
  return (
    <Card title="Fleet health">
      <div className="flex items-baseline gap-3">
        <span className="text-4xl font-semibold" style={{ color }}>{health.score}</span>
        <span className="text-sm text-[var(--color-text-secondary)]">/ 100 · tier-weighted</span>
      </div>
      <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{health.healthyBand.label}</p>
    </Card>
  );
}

export default FleetHealthTile;
